import React, {
  useState,
} from 'react';
import {
  selection,
} from 'redux-resource-plugins';
import {
  Link,
} from 'react-router';
import {
  toast,
} from 'react-toastify';

import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';

import {
  Button,
  Select,
  Checkbox,
  NavigationTabs,
  ContentWrapper,
  CreateProductModal,
  ReviewProductModal,
} from 'web-components';

import {
  Text,
  Flex,
  SVG,
  TextBreadcrumbs,
} from 'web-styled';

import EmptyState from '../EmptyState';
import { getAmount, getTabs } from '../utils';

import * as S from './styled';

const mapState = state => {
  const products = coreSelectors.getSelectionResourceMappedList(
    'products',
  )(state);
  const productPlans = coreSelectors.getSelectionResourceMappedList(
    'productPlans',
  )(state);

  const items = [
    ...products,
    ...productPlans,
  ].filter(item => item.active);
  const productOptions = coreSelectors.getEntitySearchOptions(
    'products',
  )(state);
  const productPlanOptions = coreSelectors.getEntitySearchOptions(
    'productPlans',
  )(state);

  return {
    items: items.sort(coreSelectors.alphSortByName),
    selectedItems: items.filter(item => item.selected),
    searchOptions: [
      ...productOptions,
      ...productPlanOptions,
    ],
  };
};

type View = { id: number, type: 'products' | 'productPlans' };
const Products = () => {
  const [view, setView] = useState<View | null>(null);
  const [createProductVisible, setCreateProductVisible] = useState<boolean>(false);
  const {
    items,
    selectedItems,
    searchOptions,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const handleArchive = (selected: any[]) => {
    const onConfirmAction = coreActions.requestUpdateItems({
      resources: {
        products: selected
          .filter(item => !item.interval)
          .map(item => ({ id: item.id, active: false })),
        productPlans: selected
          .filter(item => item.interval)
          .map(item => ({ id: item.id, active: false })),
      },
    });

    const singular = selected.length > 1 ? 's' : '';

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: `Archive item${singular}?`,
          bodyText: `Are you sure you want to archive item${singular}?`,
          confirmText: 'Archive',
        },
        onSuccess: () => {
          toast.success(`Product${singular} has been archived`);
        },
        onConfirmAction,
      }),
    );
  };

  const handleSelect = (id: number, type: string) => {
    dispatch(
      selection.selectResources(
        type,
        [id],
      ),
    );
  };

  const handleDeSelect = (id: number, type: string) => {
    dispatch(
      selection.deselectResources(
        type,
        [id],
      ),
    );
  };

  const selectAll = (selected: boolean) => {
    const productIds = items
      .filter(item => !item.interval)
      .map(item => item.id);
    const productPlanIds = items
      .filter(item => item.interval)
      .map(item => item.id);

    const method = selected ? 'deselectResources' : 'selectResources';

    dispatch(
      selection[method](
        'products',
        productIds,
      ),
    );

    dispatch(
      selection[method](
        'productPlans',
        productPlanIds,
      ),
    );
  };

  return (
    <ContentWrapper
      header={(
        <Flex
          width="100%"
          height="50px"
          align="center"
          justify="space-between"
        >
          <Flex spacing={10}>
            <TextBreadcrumbs>
              Billing
            </TextBreadcrumbs>

            <Button
              icon={<SVG.Plus size={14} strokeWidth={4} />}
              appearance="success"
              onClick={() => setCreateProductVisible(true)}
            >
              Create Product
            </Button>

            {selectedItems.length > 0 && (
              <Flex align="center" spacing={5}>
                <Button
                  ghost
                  appearance="danger"
                  onClick={() => handleArchive(selectedItems)}
                >
                  Archive
                </Button>
                <Text
                  size="12px"
                  color="link"
                  weight={500}
                >
                  {selectedItems.length}
                  {' Item(s)'}
                </Text>
              </Flex>
            )}
          </Flex>
          <NavigationTabs
            tabs={getTabs()}
          />
        </Flex>
      )}
    >
      <Flex fillSpace column>
        {(items.length > 0) ? (
          <Flex column scrollY fillSpace>
            <S.Search>
              <Select
                options={searchOptions}
                placeholder="Search products"
                styles={{
                  menuList: provided => ({
                    ...provided,
                    maxHeight: '316px',
                  }),
                  control: provided => ({
                    ...provided,
                    boxShadow: 'none',
                    borderColor: 'hsl(0,0%,80%)',
                  }),
                }}
                onChange={(item) => {
                  if (item?.value) {
                    setView({
                      id: Number(item.value),
                      type: item.rest.interval ? 'productPlans' : 'products',
                    });
                  }
                }}
              />
            </S.Search>
            <S.Header>
              <Flex spacing={10} align="center">
                {items.length > 0 && (
                  <Checkbox
                    checked={items.every(p => p.selected)}
                    onChange={() => {
                      selectAll(items.every(p => p.selected));
                    }}
                  />
                )}
                <Text size="18px" color="#9ea0a1" transform="capitalize">
                  Products
                </Text>
              </Flex>
            </S.Header>
            <S.Item padding="10px 15px">
              <Flex
                align="center"
                width="30%"
                shrink="0"
                padding="0 23px"
              >
                <S.HeaderName>
                  Name
                </S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="10%"
                shrink="0"
              >
                <S.HeaderName>Price</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="10%"
                shrink="0"
              >
                <S.HeaderName>Billing Type</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="40%"
                shrink="0"
              >
                <S.HeaderName>Link</S.HeaderName>
              </Flex>
            </S.Item>
            {items.map(item => (
              <S.Item
                key={item.id}
                align="center"
                padding="15px"
              >
                <Flex
                  align="center"
                  width="30%"
                  shrink="0"
                  spacing={10}
                >
                  <Checkbox
                    checked={item.selected}
                    onChange={() => {
                      const type = item.interval ? 'productPlans' : 'products';
                      if (item.selected) {
                        handleDeSelect(item.id, type);
                      } else {
                        handleSelect(item.id, type);
                      }
                    }}
                  />
                  <Text size="14px" color="link">
                    {item.name}
                  </Text>
                </Flex>
                <Flex
                  align="center"
                  width="10%"
                  shrink="0"
                >
                  <Text size="14px" color="#9ea0a1">
                    {`$${item.amount ? getAmount(item.amount) : 0}`}
                  </Text>
                </Flex>
                <Flex
                  align="center"
                  width="10%"
                  shrink="0"
                >
                  {item.interval ? (
                    <Text size="14px" color="#9ea0a1">
                      {item.interval === 'month' ? 'monthly' : 'annual'}
                    </Text>
                  ) : (
                    <Text size="14px" color="#9ea0a1">
                      one time
                    </Text>
                  )}
                </Flex>
                <Flex
                  align="center"
                  width="40%"
                >
                  {item.slug && (
                    <Link to={`/${item.slug}`}>
                      <Text color="link">
                        {`${window.location.host}/${item.slug}`}
                      </Text>
                    </Link>
                  )}
                </Flex>
                <Flex
                  align="center"
                  width="10%"
                  justify="flex-end"
                  shrink="0"
                >
                  <Flex className="actions" spacing={10}>
                    <Button
                      ghost
                      appearance="primary"
                      onClick={() => {
                        setView({
                          id: item.id,
                          type: item.interval ? 'productPlans' : 'products',
                        });
                      }}
                    >
                      View
                    </Button>
                    <Button
                      ghost
                      appearance="danger"
                      onClick={() => handleArchive([item])}
                    >
                      Archive
                    </Button>
                  </Flex>
                </Flex>
              </S.Item>
            ))}
          </Flex>
        ) : (
          <EmptyState
            text="
              Products you can sell include workout programs,
              packages, and services like Online Coaching. <br />
              Each item will have a webpage where users can view
              additional product information and complete their purchase. <br />
              Try creating your first product now."
            buttonText="Create Product"
            onClick={() => setCreateProductVisible(true)}
          />
        )}
      </Flex>

      <ReviewProductModal
        id={view?.id}
        isOpen={!!view?.id}
        resourceType={view?.type || 'products'}
        onRequestClose={() => setView(null)}
      />

      <CreateProductModal
        isOpen={createProductVisible}
        onRequestCreate={async (data) => {
          await new Promise((resolve, reject) => {
            const { type, ...payload } = data;
            dispatch(
              coreActions.requestAddEntity({
                payload,
                resourceType: type === 'onetime' ? 'products' : 'productPlans',
                callbacks: {
                  onError: reject,
                  onSuccess: resolve,
                },
              }),
            );
          });
        }}
        onRequestClose={() => setCreateProductVisible(false)}
      />
    </ContentWrapper>
  );
};

export default Products;
